import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Box,
  IconButton,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";
import {
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  PersonOutline as PersonIcon,
  AccessTime as TimeIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

import { useSidebar } from "../../context/SidebarContext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import { AppDispatch } from "../../store";
import { fetchMonitoringShifts, fetchShiftBoardStats } from "../../modules/shift/actions";
import { selectMonitoringShifts, selectShiftBoardStatsData, selectMonitoringShiftsCount } from "../../modules/shift/selectors";
import { fetchFacilityList } from "../../modules/facility/actions";
import { fetchWorkerList } from "../../modules/worker/actions";
import { fetchS3File } from "../../modules/file/actions";

import {
  PageContainer,
  BoardContainer,
  FiltersContainer,
  DateNavigator,
  DateText,
  MonitoringGrid,
  GridHeaderCell,
  ShiftCard,
  CardTime,
  CardRow,
  CardText,
  LegendContainer,
  LegendItem,
  LegendColor,
  LegendLabel,
  DayColumn,
  BoardWrapper,
  LeftSidePanel,
  RightContentPanel,
  SideInfoCard,
  SideIconWrapper,
  SideNameText,
  SideIdText,
  StatRow,
  StatLabel,
  StatValue,
} from "./styles";
import CreateShiftModal from "./components/CreateShiftModal";
import ShiftDetailContent from "./components/ShiftDetailContent";
import DetailModal from "../../components/DetailModal";
import AlertPopup from "../../components/common/AlertPopup";
import PaginatedSearchSelect from "../../components/PaginatedSearchSelect";


import CustomDateRangePicker from "../../components/CustomDateRangePicker";

const PAGE_SIZE = 2000;

interface Option {
  id: number;
  label: string;
}

const OrgShiftMonitoringBoardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage, setIsCollapsed } = useSidebar();
  const { setIsShow } = useBreadcrumb();

  // Redux State
  const shifts = useSelector(selectMonitoringShifts);
  const totalCount = useSelector(selectMonitoringShiftsCount);
  // Add loading selector (assuming selectMonitoringShiftsLoading exists or direct access)
  const isLoading = useSelector((state: any) => state.monitoringShifts?.loading);

  const gridRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [viewType, setViewType] = useState<"fortnight" | "week" | "month" | "custom">("fortnight");
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const [currentDate, setCurrentDate] = useState(dayjs().day() === 0 ? dayjs().subtract(6, 'day').startOf('day') : dayjs().startOf('week').add(1, 'day'));
  const [selectedFacility, setSelectedFacility] = useState<Option | null>(null);
  const [selectedWorker, setSelectedWorker] = useState<Option | null>(null);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [selectedShiftStatus, setSelectedShiftStatus] = useState<string>("");
  const [selectedShiftFacilityName, setSelectedShiftFacilityName] = useState<string>("");
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editShiftData, setEditShiftData] = useState<any>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ date: dayjs.Dayjs, time: dayjs.Dayjs } | null>(null);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [facilityProfilePic, setFacilityProfilePic] = useState<string | null>(null);
  const [workerProfilePic, setWorkerProfilePic] = useState<string | null>(null);

  // Update selectedShiftStatus if the shift data changes (sync header chip)
  useEffect(() => {
    if (selectedShiftId && shifts) {
      const currentShift = shifts.find(s => s.id === selectedShiftId);
      if (currentShift && currentShift.status !== selectedShiftStatus) {
        setSelectedShiftStatus(currentShift.status);
      }
    }
  }, [shifts, selectedShiftId, selectedShiftStatus]);

  const statsData = useSelector(selectShiftBoardStatsData);

  // Calculate actual start Date and end Date based on viewType
  const { actualStart, actualEnd, daysToShow } = useMemo(() => {
    let start = currentDate;
    let end: dayjs.Dayjs;
    let days = 14;

    if (viewType === 'custom' && customRange[0] && customRange[1]) {
      start = customRange[0];
      end = customRange[1];
      days = end.diff(start, 'day') + 1;
    } else {
      if (viewType === 'week') days = 7;
      else if (viewType === 'month') days = 30; // 30 days window for month view
      else days = 14; // 'fortnight' fallback

      end = start.add(days - 1, 'day');
    }

    return { actualStart: start, actualEnd: end, daysToShow: Math.max(days, 1) }; // Ensure at least 1 day
  }, [viewType, currentDate, customRange]);

  // Format dates for API
  const startDate = actualStart.format('YYYY-MM-DD');
  const endDateStr = actualEnd.format('YYYY-MM-DD');

  // Fetch initial facility selection
  useEffect(() => {
    const loadDefaultFacility = async () => {
      try {
        const response: any = await dispatch(fetchFacilityList({
          page: 1,
          pageSize: 1,
          search: ""
        })).unwrap();

        if (response.results && response.results.length > 0) {
          // const firstFacility = response.results[0];
          // setSelectedFacility({
          //   id: firstFacility.facility_id,
          //   label: firstFacility.facility_name
          // });
        }
      } catch (error) {
        console.error("Failed to load default facility", error);
      }
    };
    loadDefaultFacility();
  }, [dispatch]);

  // Fetch Data on Change
  useEffect(() => {
    dispatch(fetchMonitoringShifts({
      start_date: startDate,
      end_date: endDateStr,
      // Pass the facility ID if selected, otherwise undefined which acts as 'All'
      facility: selectedFacility ? selectedFacility.id : undefined,
      worker_id: selectedWorker ? selectedWorker.id : undefined,
      page_size: PAGE_SIZE,
      page: page
    }));
  }, [dispatch, startDate, endDateStr, selectedFacility, selectedWorker, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDateStr, selectedFacility, selectedWorker]);

  const refreshStats = React.useCallback(() => {
    if (selectedFacility || selectedWorker) {
      dispatch(fetchShiftBoardStats({
        facility_id: selectedFacility ? selectedFacility.id : undefined,
        worker_id: selectedWorker ? selectedWorker.id : undefined,
        start_date: startDate,
        end_date: endDateStr
      })).catch((err: any) => console.error("Failed to fetch stats", err));
    }
  }, [dispatch, selectedFacility, selectedWorker, startDate, endDateStr]);

  // Initial Fetch & Update on Filter Change
  useEffect(() => {
    refreshStats();
  }, [refreshStats]);

  // Scroll Handler for Grid (Infinite Loading)
  useEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = gridElement;

      // Check vertical scroll
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (!isLoading) {
          const maxPage = Math.ceil(totalCount / PAGE_SIZE);
          if (page < maxPage) {
            setPage(prev => prev + 1);
          }
        }
      }

      // Check horizontal scroll
      if (scrollLeft + clientWidth >= scrollWidth - 50) {
        if (!isLoading) {
          const maxPage = Math.ceil(totalCount / PAGE_SIZE);
          if (page < maxPage) {
            setPage(prev => prev + 1);
          }
        }
      }
    };

    gridElement.addEventListener('scroll', handleScroll);
    return () => gridElement.removeEventListener('scroll', handleScroll);
  }, [page, totalCount, isLoading]);

  // Fetch Profile Pictures when statsData changes
  useEffect(() => {
    // Reset
    setFacilityProfilePic(null);
    setWorkerProfilePic(null);

    if (statsData?.facility_stats?.profile_picture) {
      dispatch(fetchS3File({ filePath: statsData.facility_stats.profile_picture }))
        .unwrap()
        .then((blob: Blob) => {
          setFacilityProfilePic(URL.createObjectURL(blob));
        })
        .catch(err => console.error("Failed to load facility logo", err));
    }

    if (statsData?.worker_stats?.profile_picture) {
      dispatch(fetchS3File({ filePath: statsData.worker_stats.profile_picture }))
        .unwrap()
        .then((blob: Blob) => {
          setWorkerProfilePic(URL.createObjectURL(blob));
        })
        .catch(err => console.error("Failed to load worker profile pic", err));
    }

    // Cleanup generated URLs to avoid memory leaks
    return () => {
      // Need to store URLs in refs if we want to revoke them here strictly, 
      // but for simplicity in this context, standard React state update flow suffices. 
      // Ideally: if (facilityProfilePic) URL.revokeObjectURL(facilityProfilePic);
    };
  }, [dispatch, statsData]);

  // Using Redux Data
  // Map API data to component safe shape
  const allShifts = React.useMemo(() => shifts.map(s => ({
    ...s,
    id: s.id.toString(),
    start: s.start,
    end: s.end,
    workerName: (s.worker_details && Array.isArray(s.worker_details) && s.worker_details.length > 0)
      ? (s.worker_details.length > 1 ? `${s.worker_details.length} Workers Assigned` : s.worker_details[0].name)
      : (s.shift_assignment && s.shift_assignment.length > 0 && s.shift_assignment[0].worker?.user
        ? `${s.shift_assignment[0].worker.user.first_name} ${s.shift_assignment[0].worker.user.last_name}`
        : s.assigned_workers || "Unconfirmed"),
    workerId: (s.worker_details && Array.isArray(s.worker_details) && s.worker_details.length > 0)
      ? (s.worker_details.length > 1 ? "-" : s.worker_details[0].id)
      : (s.shift_assignment && s.shift_assignment.length > 0 && s.shift_assignment[0].worker
        ? s.shift_assignment[0].worker.worker_id
        : "-"),
    role: s.role_name || (Array.isArray(s.worker_details) && s.worker_details.length > 0 ? s.worker_details[0].role : null) || "-", // Fallback
    facilityName: s.facility_name,
    hours: dayjs(s.end).diff(dayjs(s.start), "hour", true).toFixed(1),
    apiStatus: s.status, // Preserve original API status
    status: (() => {
      // Direct Statuses from Backend
      if (['rejected', 'partially_rejected'].includes(s.status)) return s.status;

      const isPast = dayjs(s.end).isBefore(dayjs());

      // Check for facility_reject in worker_details
      if (s.worker_details && Array.isArray(s.worker_details)) {
        if (s.worker_details.some((w: any) => w.shift_status === 'facility_reject')) {
          return 'facility_reject';
        }
      }

      // Check for strictly defined pending statuses that should NOT default to 'unassigned'
      if (['partially_approval_pending', 'approval_pending'].includes(s.status)) {
        return 'facility_approval_pending';
      }

      if (!s.worker_details && s.status !== 'cancelled' && !s.status.includes('completed')) return 'unassigned';

      if (s.status === 'partially_absent') return 'partially_absent';

      const isAssignedOnly = ['assigned', 'scheduled', 'upcoming', 'pending', 'absent'].includes(s.status);
      if (s.worker_details && isPast && isAssignedOnly) {
        return 'absent';
      }

      if (isPast && s.status !== 'cancelled' && s.status !== 'unassigned') return 'completed';

      // Normalize started-type statuses to 'started' for consistent coloring
      if (['ongoing', 'clocked_in'].includes(s.status)) return 'started';

      return s.status;
    })(),
    is_sleepover: s.is_sleepover,
    label: s.label,
  })), [shifts]);

  // Derived sorted shifts for Grid
  // Removed old client-side stats calculation


  // Derived filtered shifts for stats and display (Main Grid)
  const filteredShifts = useMemo(() => {
    return allShifts.filter(shift =>
      (!selectedFacility || shift.facilityName === selectedFacility.label)
    );
  }, [allShifts, selectedFacility]);

  useEffect(() => {
    setActivePage("shift-board"); // Updated page key
    setIsShow(false);
    setIsCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrevDays = () => {
    if (viewType === 'month') setCurrentDate(currentDate.subtract(30, 'day'));
    else if (viewType === 'week') setCurrentDate(currentDate.subtract(7, 'day'));
    else if (viewType === 'fortnight') setCurrentDate(currentDate.subtract(14, 'day'));
    else if (viewType === 'custom' && customRange[0] && customRange[1]) {
      const days = customRange[1].diff(customRange[0], 'day') + 1;
      setCustomRange([customRange[0].subtract(days, 'day'), customRange[1].subtract(days, 'day')]);
    }
  };

  const handleNextDays = () => {
    if (viewType === 'month') setCurrentDate(currentDate.add(30, 'day'));
    else if (viewType === 'week') setCurrentDate(currentDate.add(7, 'day'));
    else if (viewType === 'fortnight') setCurrentDate(currentDate.add(14, 'day'));
    else if (viewType === 'custom' && customRange[0] && customRange[1]) {
      const days = customRange[1].diff(customRange[0], 'day') + 1;
      setCustomRange([customRange[0].add(days, 'day'), customRange[1].add(days, 'day')]);
    }
  };

  // Helper to generate header days
  const days: dayjs.Dayjs[] = [];
  for (let i = 0; i < daysToShow; i++) {
    days.push(actualStart.add(i, 'day'));
  }

  const endDate = days[days.length - 1] || actualEnd;
  const dateRangeString = `${actualStart.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;

  // Filter shifts from pre-calculated list
  const getShiftsForDate = (date: dayjs.Dayjs) => {
    return filteredShifts.filter(shift =>
      dayjs(shift.start).isSame(date, 'day')
    ).sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));
  };

  const handleColumnClick = (date: dayjs.Dayjs) => {
    setEditShiftData(null); // Clear edit data for new shift
    const time = dayjs(date).hour(9).minute(0); // Default to 9 AM
    setSelectedSlot({ date, time });
    setIsModalOpen(true);
  };

  const handleEditShift = (shift: any) => {
    setEditShiftData(shift);
    setIsDetailModalOpen(false);
    setIsModalOpen(true);
  };

  const refreshGrid = () => {
    dispatch(fetchMonitoringShifts({
      start_date: startDate,
      end_date: endDateStr,
      facility: selectedFacility ? selectedFacility.id : undefined,
      worker_id: selectedWorker ? selectedWorker.id : undefined,
      page_size: PAGE_SIZE,
      page: page
    }));
    refreshStats();
  };

  const handleShiftCreateSuccess = (msg?: string) => {
    setAlert({ message: msg || (editShiftData ? "Shift updated successfully!" : "Shift(s) created successfully!"), severity: "success" });
    setEditShiftData(null); // Reset edit mode
    // Refresh Data
    refreshGrid();
  };

  // Adapters for PaginatedSearchSelect
  const fetchFacilityOptions = React.useCallback(async (query: string, page: number) => {
    try {
      const response: any = await dispatch(fetchFacilityList({
        page: page,
        pageSize: 20,
        search: query
      })).unwrap();

      const options = response.results.map((f: any) => ({
        id: f.facility_id,
        label: f.facility_name
      }));

      return {
        options,
        hasMore: response.next !== null // Or calculate based on count
      };
    } catch (error) {
      console.error("Failed to fetch facilities", error);
      return { options: [], hasMore: false };
    }
  }, [dispatch]);

  const fetchWorkerOptions = React.useCallback(async (query: string, page: number) => {
    try {
      const response: any = await dispatch(fetchWorkerList({
        page: page,
        pageSize: 20,
        searchTerm: query,
        status: 'active'
      })).unwrap();

      const options = response.results.map((w: any) => ({
        id: w.worker_id,
        label: `${w.user.first_name} ${w.user.last_name}`
      }));

      return {
        options,
        hasMore: response.count > page * 20
      };
    } catch (error) {
      console.error("Failed to fetch workers", error);
      return { options: [], hasMore: false };
    }
  }, [dispatch]);

  return (
    <PageContainer>
      <BoardWrapper>
        {/* Left Side Panel - visible only when facility OR worker is selected */}
        {(selectedFacility || selectedWorker) && (
          <LeftSidePanel>

            {/* Facility Card */}
            {selectedFacility && (
              <SideInfoCard>
                {facilityProfilePic ? (
                  <Avatar
                    src={facilityProfilePic}
                    alt={selectedFacility.label}
                    sx={{ width: 64, height: 64, marginBottom: 2 }}
                  />
                ) : (
                  <SideIconWrapper>
                    <BusinessIcon sx={{ fontSize: 32 }} />
                  </SideIconWrapper>
                )}

                <SideNameText>
                  {selectedFacility.label}
                </SideNameText>

                <SideIdText>
                  ID: {selectedFacility.id}
                </SideIdText>

                <StatRow>
                  <StatLabel>Total Shifts</StatLabel>
                  <StatValue>{statsData?.facility_stats?.total_shifts || 0}</StatValue>
                </StatRow>

                <StatRow>
                  <StatLabel>Total Hours</StatLabel>
                  <StatValue>{statsData?.facility_stats?.total_hours || 0} h</StatValue>
                </StatRow>
              </SideInfoCard>
            )}

            {/* Worker Card */}
            {selectedWorker && (
              <SideInfoCard>
                {workerProfilePic ? (
                  <Avatar
                    src={workerProfilePic}
                    alt={selectedWorker.label}
                    sx={{ width: 64, height: 64, marginBottom: 2 }}
                  />
                ) : (
                  <SideIconWrapper>
                    <PersonIcon sx={{ fontSize: 32 }} />
                  </SideIconWrapper>
                )}

                <SideNameText>
                  {selectedWorker.label}
                </SideNameText>

                <SideIdText>
                  ID: {selectedWorker.id}
                </SideIdText>

                <StatRow>
                  <StatLabel>Total Shifts</StatLabel>
                  <StatValue>{statsData?.worker_stats?.total_shifts || 0}</StatValue>
                </StatRow>

                <StatRow>
                  <StatLabel>Total Hours</StatLabel>
                  <StatValue>{statsData?.worker_stats?.total_hours || 0} h</StatValue>
                </StatRow>
              </SideInfoCard>
            )}

          </LeftSidePanel>
        )}

        <RightContentPanel>
          <BoardContainer elevation={0}>
            {/* Filters Row */}
            <FiltersContainer>
              <DateNavigator>
                <IconButton size="small" onClick={handlePrevDays}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <DateText>{dateRangeString}</DateText>
                <IconButton size="small" onClick={handleNextDays}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </DateNavigator>

              <Box display="flex" alignItems="center" gap={1.5}>
                <Box width={120}>
                  <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
                    <Select
                      value={viewType}
                      onChange={(e) => {
                        const newView = e.target.value as any;
                        setViewType(newView);
                        if (newView === 'week') {
                          setCurrentDate(dayjs().startOf('week').add(1, 'day'));
                        } else if (newView === 'month') {
                          setCurrentDate(dayjs().startOf('month'));
                        } else if (newView === 'fortnight') {
                          setCurrentDate(dayjs().day() === 0 ? dayjs().subtract(6, 'day').startOf('day') : dayjs().startOf('week').add(1, 'day'));
                        }
                      }}
                      displayEmpty
                      sx={{ borderRadius: '8px', backgroundColor: '#fff', height: '36px', fontSize: '0.85rem' }}
                      MenuProps={{
                        disablePortal: true,
                        autoFocus: false,
                        sx: {
                          position: 'absolute !important',
                          top: '100% !important',
                          left: '0 !important',
                          right: '0 !important',
                          width: '100% !important',
                          transform: 'none !important',
                          marginTop: '4px',
                          zIndex: 1300,
                          '& .MuiPaper-root': {
                            position: 'relative !important',
                            top: 'auto !important',
                            left: 'auto !important',
                            transform: 'none !important',
                            maxHeight: 300,
                          }
                        }
                      }}
                    >
                      <MenuItem value="fortnight">Fortnight</MenuItem>
                      <MenuItem value="week">Week</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {viewType === "custom" && (
                  <Box width={240}>
                    <CustomDateRangePicker
                      value={customRange}
                      onChange={(newValue) => setCustomRange(newValue)}
                    />
                  </Box>
                )}

                <Box width={200}>
                  <PaginatedSearchSelect<Option>
                    label=""
                    placeholder="Search Facility"
                    fetchOptions={fetchFacilityOptions}
                    getOptionLabel="label"
                    value={selectedFacility}
                    onChange={(newValue) => setSelectedFacility(newValue)}
                  />
                </Box>

                <Box width={200}>
                  <PaginatedSearchSelect<Option>
                    label=""
                    placeholder="Search Worker"
                    fetchOptions={fetchWorkerOptions}
                    getOptionLabel="label"
                    value={selectedWorker}
                    onChange={(newValue) => setSelectedWorker(newValue)}
                  />
                </Box>
              </Box>
            </FiltersContainer>

            {/* Grid */}
            <MonitoringGrid ref={gridRef} daysCount={daysToShow}>
              {/* Header Row */}
              {days.map((day) => (
                <GridHeaderCell key={day.toString()}>
                  {day.format("ddd D")}
                </GridHeaderCell>
              ))}

              {/* Day Columns */}
              {days.map((day) => {
                const dayShifts = getShiftsForDate(day);

                return (
                  <DayColumn
                    key={`col-${day.format('DD')}`}
                    onClick={() => handleColumnClick(day)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f9fafb' }
                    }}
                  >
                    {dayShifts.map((shift, index) => {
                      // Gap Logic
                      let marginTop = '12px';
                      if (index === 0) {
                        marginTop = '0px';
                      } else {
                        const prevShift = dayShifts[index - 1];
                        const prevEnd = dayjs(prevShift.end);
                        const currStart = dayjs(shift.start);
                        if (prevEnd.isAfter(currStart) || prevEnd.isSame(currStart)) {
                          marginTop = '4px';
                        }
                      }

                      return (
                        <ShiftCard
                          key={shift.id}
                          status={shift.status}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedShiftId(Number(shift.id));
                            setSelectedShiftStatus(shift.status);
                            setSelectedShiftFacilityName(shift.facilityName);
                            setIsDetailModalOpen(true);
                          }}
                          sx={{
                            marginTop: marginTop,
                            marginBottom: '0px', // Override default
                          }}
                        >
                          <CardTime>
                            {dayjs(shift.start).format("h:mm A")} - {dayjs(shift.end).format("h:mm A")}
                          </CardTime>
                          <CardRow>
                            <PersonIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                            <CardText>{shift.workerName}</CardText>
                          </CardRow>
                          <CardRow>
                            <TimeIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                            <CardText>{shift.hours} hours</CardText>
                          </CardRow>
                          <CardText sx={{ marginTop: '4px', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' }}>
                            {shift.status === 'facility_reject'
                              ? 'Facility Reject'
                              : shift.status === 'rejected'
                                ? 'Rejected'
                                : shift.status === 'partially_rejected'
                                  ? 'Partially Rejected'
                                  : shift.status === 'facility_approval_pending'
                                    ? 'Facility Approval Pending'
                                    : shift.apiStatus === 'upcoming' ? 'Scheduled' : shift.apiStatus === 'unassigned' ? 'Unconfirmed' : shift.apiStatus.replace(/_/g, ' ')}
                          </CardText>
                          {shift.is_sleepover && (
                            <CardText sx={{ marginTop: '4px', fontWeight: 800, color: '#fff', backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 4px', borderRadius: '4px', width: 'fit-content', fontSize: '10px' }}>
                              SLEEPOVER SHIFT
                            </CardText>
                          )}
                          {shift.label && (
                            <CardRow sx={{ mt: 0.5 }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>
                                {shift.label}
                              </Typography>
                            </CardRow>
                          )}
                          <CardText sx={{ marginTop: '4px', fontStyle: 'italic', fontSize: '11px' }}>
                            {shift.facilityName}
                          </CardText>
                        </ShiftCard>
                      );
                    })}
                  </DayColumn>
                );
              })}
            </MonitoringGrid>

            {/* Legend */}
            <LegendContainer>
              {[
                { label: "Completed", color: "#479577" },
                { label: "Started", color: "#66bb6a" },
                { label: "Facility Approval Pending", color: "#8D6E63" },
                { label: "Facility Reject", color: "#C62828" },
                { label: "Scheduled", color: "#4A90E2" },
                { label: "Unconfirmed", color: "#D2A627" },
                { label: "Cancelled", color: "#FF4D4F" },
                { label: "Absent", color: "#FF9800" },
                { label: "Rejected", color: "#C62828" },
              ].map((item) => (
                <LegendItem key={item.label}>
                  <LegendColor color={item.color} />
                  <LegendLabel>{item.label}</LegendLabel>
                </LegendItem>
              ))}
            </LegendContainer>

          </BoardContainer >
        </RightContentPanel>
      </BoardWrapper>

      <CreateShiftModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleShiftCreateSuccess}
        initialDate={selectedSlot?.date}
        initialTime={selectedSlot?.time}
        initialFacility={selectedFacility}
        editData={editShiftData}
      />
      <DetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        shiftId={selectedShiftId || 0}
        shiftStatus={selectedShiftStatus}
      >
        <ShiftDetailContent
          shiftId={selectedShiftId}
          facilityName={selectedShiftFacilityName}
          onEdit={handleEditShift}
          onRefresh={refreshGrid}
        />
      </DetailModal>
      <AlertPopup
        open={!!alert}
        message={alert?.message || ''}
        severity={alert?.severity || 'success'}
        onClose={() => setAlert(null)}
      />
    </PageContainer >
  );
};

export default OrgShiftMonitoringBoardPage;
